import type { BriefOptionsMap } from '@/lib/brief-options';
import type { PrefKey, Preferences } from '@/lib/types';

/**
 * Focused POI / commute chips — "Schools near Brigade Eldorado", "schools nearby",
 * "Schools near my matches". These are project (or shortlist) facet asks, NOT
 * area pivots. Without this gate, `near <project>` and the substring `my matches`
 * clear board focus and the next turn searches apartments in Bengaluru.
 */
export function isFocusedPoiAsk(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (/^schools?\s+near\s+(?:my\s+matches|this\s+project)\b/i.test(t)) return true;
  if (/\b(?:schools?|hospitals?|clinics?|colleges?|metro|amenities)\s+near\b/i.test(t)) return true;
  if (/\b(?:nearby|near\s+me)\s+(?:schools?|hospitals?|clinics?|colleges?)\b/i.test(t)) return true;
  if (/^schools?\s+nearby\??$/i.test(t)) return true;
  if (/^commute\s+from\b/i.test(t)) return true;
  return false;
}

/** Buyer wants the matches hub — clear project focus and show the list. */
export function isExploreMoreTurn(text: string): boolean {
  if (isFocusedPoiAsk(text)) return false;
  return /\b(?:show me other|show me more|other projects|more options|more projects|back to (?:all )?matches|my matches)\b/i.test(
    text,
  );
}

/** User line from refineSearch / refine chips — keep matches hub unfocused. */
export function isSearchRefinementUserLine(text: string): boolean {
  const t = text.trim();
  if (/^show me projects\b/i.test(t)) return true;
  if (/^show me .+ projects\b/i.test(t)) return true;
  if (/\bopen to any area\b/i.test(t)) return true;
  return false;
}

export function shouldClearMatchFocus(text: string, locations: readonly string[]): boolean {
  if (isFocusedPoiAsk(text)) return false;
  return (
    isExploreMoreTurn(text) ||
    isSearchRefinementUserLine(text) ||
    isFocusedSearchPivot(text) ||
    !!extractLocationRefinement(text, locations) ||
    !!extractLooseLocationPivot(text)
  );
}

/** Bot asked the buyer to widen or change search criteria. */
export function exploreMoreRefineReply(prefs: Preferences): string {
  const parts: string[] = [];
  if (prefs.location) parts.push('area');
  if (prefs.budget) parts.push('budget');
  parts.push('unit configuration');
  const joined =
    parts.length > 2
      ? `${parts.slice(0, -1).join(', ')}, or ${parts[parts.length - 1]}`
      : parts.join(' or ');
  return `No projects match that combination yet. Want to widen ${joined}? Tap a chip below or tell me what to change.`;
}

export function refineSearchAck(prefs: Preferences, count: number): string {
  const summaryParts = [
    prefs.propertyTypes.length ? prefs.propertyTypes.join(' · ') : null,
    prefs.bhkSelections.length ? prefs.bhkSelections.join(' · ') : prefs.bhk,
    prefs.location,
    prefs.budget,
  ].filter(Boolean);
  const brief = summaryParts.join(' · ');
  if (count === 1) {
    return `Updated your brief — 1 project fits ${brief}. It’s on your board.`;
  }
  return `Updated your brief — ${count} projects fit ${brief}. They’re on your matches board.`;
}

function normalize(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

function cleanLocalityFragment(raw: string): string {
  return raw.trim().replace(/\s+(?:too|also|as well)\.?\s*$/i, '').trim();
}

/** One-character typo / prefix match against catalog facet labels. */
function nearLocationMatch(token: string, facet: string): boolean {
  const t = normalize(token);
  const f = normalize(facet);
  if (!t || !f) return false;
  if (f === t || f.includes(t) || t.includes(f)) return true;
  const parts = f.split(/[/,·]/).map((p) => p.trim()).filter((p) => p.length >= 4);
  for (const part of parts) {
    if (part === t || part.includes(t) || t.includes(part)) return true;
    if (part.length >= 5 && t.length >= 5 && part.slice(0, 5) === t.slice(0, 5)) return true;
    if (Math.abs(part.length - t.length) <= 1) {
      let diffs = 0;
      const len = Math.max(part.length, t.length);
      for (let i = 0; i < len; i++) {
        if (part[i] !== t[i]) diffs++;
        if (diffs > 1) break;
      }
      if (diffs <= 1) return true;
    }
  }
  return false;
}

function matchLocationFacet(fragment: string, locations: readonly string[]): string | null {
  const cleaned = cleanLocalityFragment(fragment);
  for (const loc of locations) {
    if (loc === 'Open to suggestions') continue;
    if (nearLocationMatch(cleaned, loc)) return loc;
  }
  return null;
}

/** Match a location facet from free text (e.g. "whitefield", "projects in Whitefield"). */
export function extractLooseLocationPivot(text: string): string | null {
  const t = normalize(text);
  if (!t || isExploreMoreTurn(text) || isFocusedPoiAsk(text)) return null;

  const lookingIn = /\b(?:looking|searching|interested)\s+(?:in|for)\s+([a-z][a-z\s-]{2,24})/i.exec(text);
  if (lookingIn?.[1]) return cleanLocalityFragment(lookingIn[1]);

  const cityProjects = /^([A-Za-z][A-Za-z\s]{2,24}?)\s+projects?\b/i.exec(text.trim());
  if (
    cityProjects?.[1] &&
    !/\b(?:show|me|other|more|the|my|all|some|any|different|find|list|see)\b/i.test(cityProjects[1])
  ) {
    return cleanLocalityFragment(cityProjects[1]);
  }

  const inMatch = /\b(?:in|near|around|at)\s+([A-Za-z][A-Za-z\s/-]{2,40})/i.exec(text);
  if (inMatch?.[1]) return cleanLocalityFragment(inMatch[1]);

  return null;
}

/** Focused-phase project Q&A — not a search pivot (mirrors ConverseSpine RTI-B). */
function isFocusedProjectQuestion(text: string): boolean {
  const t = text.trim();
  if (isFocusedPoiAsk(t)) return true;
  if (/^(?:pricing|price|legal|rera|emi|visit|amenities|location|floor plans?|availability|media|overview)\.?$/i.test(t)) {
    return true;
  }
  if (/^(?:legal|rera|pricing|price|emi|visit|amenities|availability|possession|floor plan)\b/i.test(t)) {
    return true;
  }
  if (/^location(?:\s+details?)?\s*\.?\s*$/i.test(t) || /\blocation details?\b/i.test(t)) return true;
  return (
    /\b(?:is this|are these|what (?:type|kind)|apartment or|plot or|villa or|legal status|rera status|possession date)\b/i.test(
      t,
    ) ||
    /\b(?:tell me about|what about|how (?:is|are)|does (?:it|this)|do they)\b/i.test(t)
  );
}

/** Buyer pivots area/budget/type while focused on one project card. */
export function isFocusedSearchPivot(text: string): boolean {
  const t = text.trim();
  if (!t || isFocusedProjectQuestion(t)) return false;
  if (isExploreMoreTurn(text)) return true;
  if (isSearchRefinementUserLine(text)) return true;
  if (extractLooseLocationPivot(text)) return true;
  if (/^[A-Za-z][A-Za-z\s]{2,28}\s+projects?\b/i.test(t)) return true;
  if (/\b(?:looking|searching|interested)\s+(?:in|for)\s+/i.test(t)) return true;
  if (/\b(?:change|switch|update)\s+(?:my\s+)?(?:area|location|budget|bhk|property type)\b/i.test(t)) return true;
  if (/\b(?:also|too|as well)\b/i.test(t) && /\b(?:plantation|villa|apartment|plot|bhk|budget|area)\b/i.test(t)) {
    return true;
  }
  return false;
}

/** Match a location facet from free text (e.g. "whitefield", "projects in Whitefield"). */
export function extractLocationRefinement(
  text: string,
  locations: readonly string[],
): string | null {
  const t = normalize(text);
  if (!t || isExploreMoreTurn(text)) return null;

  for (const loc of locations) {
    if (loc === 'Open to suggestions') continue;
    const key = normalize(loc);
    if (t === key || t.includes(key)) return loc;
    const parts = key.split(/[/,·]/).map((p) => p.trim()).filter((p) => p.length >= 4);
    for (const part of parts) {
      if (t === part || t.includes(part)) return loc;
    }
  }

  const propsIn = /\b(?:projects?|properties|options|homes?)\s+in\s+([a-z][a-z\s-]{2,24})/i.exec(text);
  if (propsIn?.[1]) {
    const hit = matchLocationFacet(propsIn[1], locations);
    if (hit) return hit;
  }

  // Single-token area name ("whitefield", "sarjapur")
  if (!/\s/.test(t) && t.length >= 4) {
    const hit = matchLocationFacet(t, locations);
    if (hit) return hit;
  }

  const inMatch = /\b(?:in|near|around|at)\s+([a-z0-9 /,-]{3,40})/i.exec(text);
  if (inMatch?.[1]) {
    const hit = matchLocationFacet(inMatch[1], locations);
    if (hit) return hit;
  }

  return null;
}

/** Buyer wants to add or broaden search area (including while focused on a project). */
export function isLocationExpandTurn(text: string, locations: readonly string[]): boolean {
  if (extractLocationRefinement(text, locations)) return true;
  return (
    /\b(?:projects?|properties|options|homes?)\s+in\s+/i.test(text) &&
    /\b(?:too|also|as well|want)\b/i.test(text)
  );
}

export function nextBudgetTier(
  current: string | null,
  budgets: readonly string[],
): string | null {
  if (budgets.length === 0) return null;
  if (!current) return budgets[0] ?? null;
  const i = budgets.indexOf(current);
  if (i >= 0 && i < budgets.length - 1) return budgets[i + 1] ?? null;
  return budgets[budgets.length - 1] ?? null;
}

export function isSearchRefinementTurn(text: string, locations: readonly string[]): boolean {
  if (parseRecoverySearch(text, locations)) return true;
  if (extractLocationRefinement(text, locations)) return true;
  if (/\b(?:widen|increase|raise|bump|expand)\s+(?:my\s+)?budget\b/i.test(text)) return true;
  if (/\b(?:change|switch)\s+(?:to\s+)?\d\s*bhk\b/i.test(text)) return true;
  return false;
}

/** Chat-driven search pivot after a no-match brief (e.g. "apartments in bangalore for same budget"). */
export function parseRecoverySearch(
  text: string,
  locations: readonly string[],
): Partial<Pick<Preferences, 'location' | 'propertyTypes' | 'bhkSelections'>> | null {
  const patch: Partial<Pick<Preferences, 'location' | 'propertyTypes' | 'bhkSelections'>> = {};
  let changed = false;

  if (/\b(?:apartment|flat)s?\b/i.test(text)) {
    patch.propertyTypes = ['Apartment'];
    changed = true;
  } else if (/\bvillas?\b/i.test(text)) {
    patch.propertyTypes = ['Villa'];
    changed = true;
  } else if (/\b(?:plot|land|plantation|planted)\b/i.test(text)) {
    patch.propertyTypes = ['Planted estate'];
    changed = true;
  }

  const bhkMatch = /\b(\d\s*bhk)\b/i.exec(text);
  if (bhkMatch?.[1]) {
    patch.bhkSelections = [bhkMatch[1].replace(/\s+/g, ' ')];
    changed = true;
  }

  const loc = extractLocationRefinement(text, locations);
  if (loc) {
    patch.location = loc;
    changed = true;
  } else if (/\bbangalore\b/i.test(text)) {
    const bangaloreFacet =
      locations.find((l) => /bangalore/i.test(l) && l !== 'Open to suggestions') ??
      locations.find((l) => l !== 'Open to suggestions');
    patch.location = bangaloreFacet ?? 'Open to suggestions';
    changed = true;
  }

  return changed ? patch : null;
}

/** Dynamic chips after a tight or empty search result. */
export function buildRefineChips(prefs: Preferences, options: BriefOptionsMap): string[] {
  const chips: string[] = [];
  const locs = options.location ?? [];
  const currentLoc = prefs.location;

  for (const loc of locs) {
    if (loc === currentLoc || loc === 'Open to suggestions') continue;
    chips.push(`Try ${loc}`);
    if (chips.length >= 3) break;
  }

  const budgets = options.budget ?? [];
  const nextBudget = nextBudgetTier(prefs.budget, budgets);
  if (nextBudget && nextBudget !== prefs.budget) {
    chips.push(`Budget ${nextBudget}`);
  }
  if (budgets.length > 0 && prefs.budget !== budgets[budgets.length - 1]) {
    chips.push('Widen budget');
  }

  const bhks = options.bhk ?? [];
  for (const bhk of bhks) {
    if (prefs.bhkSelections.includes(bhk)) continue;
    chips.push(bhk);
    if (chips.filter((c) => bhks.includes(c)).length >= 2) break;
  }

  if (currentLoc !== 'Open to suggestions') {
    chips.push('Open to any area');
  }

  return chips.slice(0, 6);
}

export type RefineChipAction =
  | { kind: 'location'; value: string; userLine: string }
  | { kind: 'budget'; value: string; userLine: string }
  | { kind: 'bhk'; value: string; userLine: string }
  | { kind: 'widen_budget'; value: string; userLine: string };

export function parseRefineChip(
  label: string,
  prefs: Preferences,
  options: BriefOptionsMap,
): RefineChipAction | null {
  if (label === 'Widen budget') {
    const next = nextBudgetTier(prefs.budget, options.budget ?? []);
    if (!next) return null;
    return {
      kind: 'widen_budget',
      value: next,
      userLine: `Show me projects with budget up to ${next}`,
    };
  }
  if (label === 'Open to any area') {
    return {
      kind: 'location',
      value: 'Open to suggestions',
      userLine: 'Show me projects — open to any area in Bangalore',
    };
  }
  if (label.startsWith('Try ')) {
    const value = label.slice(4).trim();
    if (!value) return null;
    return {
      kind: 'location',
      value,
      userLine: `Show me projects in ${value}`,
    };
  }
  if (label.startsWith('Budget ')) {
    const value = label.slice(7).trim();
    if (!value) return null;
    return {
      kind: 'budget',
      value,
      userLine: `Show me projects with budget ${value}`,
    };
  }
  if (options.bhk?.includes(label)) {
    return {
      kind: 'bhk',
      value: label,
      userLine: `Show me ${label} projects that fit my brief`,
    };
  }
  return null;
}

export function applyServerSuggestedPatch(
  prefs: Preferences,
  patch: Record<string, string | undefined>,
): Preferences {
  let next: Preferences = { ...prefs };
  if (patch.location !== undefined) {
    next = { ...next, location: patch.location || null };
  }
  if (patch.budget) {
    next = { ...next, budget: patch.budget };
  }
  if (patch.property_type) {
    next = {
      ...next,
      propertyTypes: [patch.property_type],
      bhkSelections: [],
      plotSizeSelections: [],
      bhk: null,
    };
  }
  if (patch.bhk === '') {
    next = { ...next, bhkSelections: [], bhk: null };
  } else if (patch.bhk) {
    next = { ...next, bhkSelections: [patch.bhk], bhk: patch.bhk };
  }
  return next;
}

export interface ServerSuggestedAction {
  id: string;
  label: string;
  patch: Record<string, string | undefined>;
  user_line: string;
  expected_matches: number;
}

/** Prefer server actions; fall back to client-built refine chips (mock/demo only). */
export function suggestedActionLabels(
  serverActions: readonly ServerSuggestedAction[] | undefined,
  prefs: Preferences,
  options: BriefOptionsMap,
  opts?: { serverActionsOnly?: boolean },
): string[] {
  if (serverActions?.length) {
    return serverActions.map((a) => a.label).slice(0, 6);
  }
  if (opts?.serverActionsOnly) return [];
  return buildRefineChips(prefs, options);
}

export function resolveSuggestedAction(
  label: string,
  serverActions: readonly ServerSuggestedAction[] | undefined,
  prefs: Preferences,
  options: BriefOptionsMap,
): { userLine: string; actionId?: string; patch: (p: Preferences) => Preferences } | null {
  const server = serverActions?.find((a) => a.label === label);
  if (server) {
    return {
      userLine: server.user_line,
      actionId: server.id,
      patch: (p) => applyServerSuggestedPatch(p, server.patch),
    };
  }
  const chip = parseRefineChip(label, prefs, options);
  if (!chip) return null;
  return {
    userLine: chip.userLine,
    patch: (p) => applyRefinePatch(p, chip),
  };
}

export function applyRefinePatch(prefs: Preferences, action: RefineChipAction): Preferences {
  switch (action.kind) {
    case 'location':
      return { ...prefs, location: action.value, ...(action.value && !prefs.timeline ? { timeline: 'Next 6 months' } : {}) };
    case 'budget':
      return { ...prefs, budget: action.value };
    case 'bhk':
      return { ...prefs, bhkSelections: [action.value], bhk: action.value };
    case 'widen_budget':
      return { ...prefs, budget: action.value };
    default:
      return prefs;
  }
}
